import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import IntraOralCameraChart from '../IntraOralCameraChart';
import {
  TIntraOralCameraChartFormValues,
  TAttachmentFiles,
} from 'types/chart/intraOralCameraChart';
import {
  IntraOralCameraChartFormDefaultValues,
  AttachmentFilesDefaultValues,
} from 'constants/chart/intraOralCameraChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import {
  TEvaluationChartInfo,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import { TChartFileInfoList } from 'types/chart';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  setChartInfoResponse: (data: any) => void;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isEdit: boolean;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
};

const IntraOralCameraChartRegister = ({
  query,
  chartType,
  category,
  id,
  isRegister,
  setSubmitEndDate,
  chartInfoResponse,
  setChartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isEdit,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
}: TProps) => {
  const { openModal } = useModal();

  const [attachmentFiles, setAttachmentFiles] = useState<TAttachmentFiles>(
    AttachmentFilesDefaultValues
  );
  const [deleteFileIdList, setDeleteFileIdList] = useState<number[]>([]);

  const { chartRegister } = useChartRegister(({ data }) => afterAutoSave(data));

  const methods = useForm<TIntraOralCameraChartFormValues>({
    defaultValues: IntraOralCameraChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const deleteFileIdListRef = useRef(deleteFileIdList);
  const attachmentFilesRef = useRef(attachmentFiles);
  const prevFormValuesRef = useRef<TIntraOralCameraChartFormValues | null>(
    null
  );
  const prevAttachmentFilesRef = useRef<TAttachmentFiles | null>(null);

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
    prevAttachmentFilesRef.current = attachmentFiles;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
    deleteFileIdListRef.current = deleteFileIdList;
    attachmentFilesRef.current = attachmentFiles;
  }, [formValues, deleteFileIdList, attachmentFiles]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        const chartInfoList = chartInfoResponse?.chartInfo as
          | TEvaluationChartInfo[]
          | undefined;
        if (
          chartInfoList &&
          chartInfoList.length > (activeTabIndex > 1 ? 1 : 0)
        ) {
          const currentChartInfo = chartInfoList[activeTabIndex > 1 ? 1 : 0];
          if (
            currentChartInfo?.chartData === null &&
            JSON.stringify(IntraOralCameraChartFormDefaultValues) ===
              JSON.stringify(formValuesRef.current)
          ) {
            return;
          }
        }

        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
            JSON.stringify(prevFormValuesRef.current) ||
          JSON.stringify(attachmentFilesRef.current) !==
            JSON.stringify(prevAttachmentFilesRef.current)
        ) {
          onValid(
            true,
            formValuesRef.current,
            deleteFileIdListRef.current,
            attachmentFilesRef.current
          );
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const afterAutoSave = ({ chartInfo }: TPutEvaluationChartResponse) => {
    setChartInfoResponse((prev: any) => {
      if (!prev) return prev;
      const newChartInfo = prev.chartInfo as TEvaluationChartInfo[];
      const newChartInfoData = [...newChartInfo];
      newChartInfoData[activeTabIndex > 1 ? 1 : 0] = {
        ...newChartInfoData[activeTabIndex > 1 ? 1 : 0],
        chartData: chartInfo?.chartData || null,
        fileInfoList: chartInfo?.fileInfoList || [],
      };
      return {
        ...prev,
        chartInfo: newChartInfoData,
      };
    });
    prevFormValuesRef.current = chartInfo?.chartData || null;
    setDeleteFileIdList([]);
    setFiles(chartInfo?.fileInfoList || [], true);
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    let chartData: TIntraOralCameraChartFormValues | null =
      (chartInfo?.chartData as TIntraOralCameraChartFormValues | null) || null;
    let filelist = chartInfo?.fileInfoList || null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);

      const prevInfo = (
        chartInfoResponse?.chartInfo as TEvaluationChartInfo[]
      )[0];
      const prevData = prevInfo?.chartData || null;
      const prevFile = prevInfo?.fileInfoList || null;
      if (prevData && activeTabIndex > 1) {
        const filteredData = Object.keys(prevData)
          .filter((key) => key.includes('1'))
          .reduce(
            (obj, key) => {
              (obj as Record<string, any>)[key] =
                prevData[key as keyof typeof prevData];
              return obj;
            },
            {} as Partial<typeof prevData>
          );
        if (!chartData) {
          chartData =
            IntraOralCameraChartFormDefaultValues as TIntraOralCameraChartFormValues;
        }
        Object.keys(filteredData).forEach((key) => {
          if (!chartData) return;
          chartData[key as keyof TIntraOralCameraChartFormValues] =
            filteredData[key as keyof typeof filteredData];
        });
      }
      if (prevFile && activeTabIndex > 1) {
        const filteredFiles = prevFile.filter(
          (file) => file.fileArrangeNum < 4
        );
        if (!filelist) {
          filelist = [];
        }
        filelist = filelist.filter((file) => file.fileArrangeNum < 4);
        filelist = [...filteredFiles, ...filelist];
      }
    }
    if (!chartData) {
      reset();
      setFiles([], false);
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      if (!chartData) return;
      setValue(
        key as keyof TIntraOralCameraChartFormValues,
        chartData[key as keyof TIntraOralCameraChartFormValues]
      );
    });

    setFiles(
      filelist?.sort((a, b) => a.fileArrangeNum - b.fileArrangeNum) || [],
      false
    );
    setIsLoading(false);
  };

  const setFiles = (
    fileInfoList: TChartFileInfoList[],
    isAutoSave: boolean
  ) => {
    const maxArrangeNum = Math.max(
      ...fileInfoList.map((file) => file.fileArrangeNum)
    );
    const newAttachmentFiles = Array.from({ length: maxArrangeNum }, (_, i) => {
      const arrangeNum = i + 1;
      const fileInfo = fileInfoList.find(
        (file) => file.fileArrangeNum === arrangeNum
      );

      if (fileInfo) {
        return new File([], fileInfo.filePath, {
          type: `fileid:${fileInfo.fileId}`,
        });
      } else {
        return null;
      }
    });
    setAttachmentFiles(newAttachmentFiles);
    if (isAutoSave) prevAttachmentFilesRef.current = newAttachmentFiles;
  };

  const changeFile = (index: number, file: File[]) =>
    setAttachmentFiles((prev) => {
      const newFiles = { ...prev };
      newFiles[index] = file[0] || null;
      return newFiles;
    });

  // 차트 조회
  const formMediaFileRefs = useRef<Array<{ resetFile: () => void } | null>>([]);

  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    formMediaFileRefs.current.forEach((ref) => {
      if (ref) {
        ref.resetFile();
      }
    });
    setChartInfo(chartInfo);
  }, [activeTabIndex]);

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TIntraOralCameraChartFormValues,
    deleteFileIdListData: number[] = deleteFileIdList,
    attachmentFilesData: TAttachmentFiles = attachmentFiles
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
            deleteFileIdList: deleteFileIdListData,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    for (let i = 0; i < 6; i++) {
      if (
        !!attachmentFilesData[i] &&
        !attachmentFilesData[i]?.type.includes('fileid')
      ) {
        formData.append(`file${i + 1}`, attachmentFilesData[i] as Blob);
      }
    }
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          <IntraOralCameraChart
            category={category}
            formMediaFileRefs={formMediaFileRefs}
            attachmentFiles={attachmentFiles}
            deleteFileIdList={deleteFileIdList}
            setDeleteFileIdList={setDeleteFileIdList}
            changeFile={changeFile}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
            activeTabIndex={activeTabIndex}
          />
          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default IntraOralCameraChartRegister;
